import React, { useEffect, useState, useRef } from 'react';
/**
 * ColumnArithmetic — Interactive column arithmetic (optellen/aftrekken/vermenigvuldigen).
 *
 * Parses the operation from the question text, renders a grid with carry
 * inputs and answer digit inputs. Supports keyboard navigation between cells.
 *
 * Co-located in practice/ domain (CL-1 s63).
 * Data-first parsing (PEDAGOGICAL_CONTRACT_SPEC §7): prefers question.data.columnArithmetic
 * over regex fallback.
 */
import type { Question } from '../../content/types';
import { warn } from '../../utils/logger';
interface ColumnArithmeticProps {
  question: Question;
  onComplete: (userAnswer: string) => void;
  onReadyChange?: (isReady: boolean) => void;
  shouldValidate?: boolean;
}
type Operation = '+' | '-' | '×';
function parseOperationFromText(text: string): {
  nums: number[];
  op: Operation;
} | null {
  const clean = text.replace(/\./g, '').replace(/,/g, '.');
  const match = clean.match(/(\d+)\s*([+\-\u2212×✕*])\s*(\d+)/);
  if (!match) return null;
  const num1 = parseInt(match[1]);
  const opChar = match[2];
  const op: Operation = opChar === '+' ? '+' : opChar === '-' || opChar === '\u2212' ? '-' : '×';
  const num2 = parseInt(match[3]);
  return {
    nums: [num1, num2],
    op
  };
}
/**
 * Data-first operation parser (PEDAGOGICAL_CONTRACT_SPEC §7).
 * Prefers structured data from question.data.columnArithmetic,
 * falls back to regex parsing with deprecation warning.
 */
function parseOperation(question: Question): {
  nums: number[];
  op: Operation;
} | null {
  const structured = question.data?.columnArithmetic;
  if (structured && typeof structured.operand1 === 'number' && typeof structured.operand2 === 'number') {
    return {
      nums: [structured.operand1, structured.operand2],
      op: structured.operator ?? '+'
    };
  }
  // Regex fallback — log deprecation warning
  const result = parseOperationFromText(question.question);
  if (result) {
    warn(`[ColumnArithmetic] Regex fallback for ${question.id} — add data.columnArithmetic to seed`);
  }
  return result;
}
function computeResult(num1: number, num2: number, op: Operation): number {
  return op === '+' ? num1 + num2 : op === '-' ? num1 - num2 : num1 * num2;
}
const OP_LABELS: Record<Operation, string> = {
  '+': 'Optellen',
  '-': 'Aftrekken',
  '×': 'Vermenigvuldigen'
};
export function ColumnArithmetic({
  question,
  onComplete,
  onReadyChange,
  shouldValidate = false
}: ColumnArithmeticProps) {
  const [numbers, setNumbers] = useState<number[]>([]);
  const [operation, setOperation] = useState<Operation>('+');
  const [userInputs, setUserInputs] = useState<string[]>([]);
  const [carryInputs, setCarryInputs] = useState<string[]>([]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const carryRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevValidateRef = useRef(false);
  // Parse question on mount/change
  useEffect(() => {
    const parsed = parseOperation(question);
    if (parsed) {
      const [num1, num2] = parsed.nums;
      setNumbers(parsed.nums);
      setOperation(parsed.op);
      const result = computeResult(num1, num2, parsed.op);
      const resultLen = result.toString().length;
      setUserInputs(new Array(resultLen).fill(''));
      const maxLen = parsed.op === '×' ? resultLen : Math.max(num1.toString().length, num2.toString().length);
      setCarryInputs(new Array(maxLen).fill(''));
    }
    prevValidateRef.current = false;
  }, [question.id]);
  // Report readiness
  useEffect(() => {
    onReadyChange?.(userInputs.every(v => v !== ''));
  }, [userInputs, onReadyChange]);
  // Reset validation flag
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  // Rising-edge validation
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onComplete(userInputs.join(''));
    }
  }, [shouldValidate, userInputs, onComplete]);
  // Mobile scroll-into-view on focus
  useEffect(() => {
    const handleFocus = () => {
      if (containerRef.current && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        setTimeout(() => containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        }), 300);
      }
    };
    const allInputs = [...inputRefs.current, ...carryRefs.current].filter(Boolean);
    allInputs.forEach(input => input?.addEventListener('focus', handleFocus));
    return () => allInputs.forEach(input => input?.removeEventListener('focus', handleFocus));
  }, [userInputs.length, carryInputs.length]);
  const handleInputChange = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '');
    const newInputs = [...userInputs];
    newInputs[index] = digit;
    setUserInputs(newInputs);
    if (digit && index < userInputs.length - 1) inputRefs.current[index + 1]?.focus();
  };
  const handleCarryChange = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '');
    const newCarries = [...carryInputs];
    newCarries[index] = digit;
    setCarryInputs(newCarries);
  };
  if (numbers.length !== 2) return <div data-id="element-7908">Laden...</div>;
  const [num1, num2] = numbers;
  const result = computeResult(num1, num2, operation);
  const resultStr = Math.abs(result).toString();
  const gridWidth = Math.max(resultStr.length, num1.toString().length, num2.toString().length);
  const paddedNum1 = num1.toString().padStart(gridWidth, ' ');
  const paddedNum2 = num2.toString().padStart(gridWidth, ' ');
  const answerOffset = gridWidth - userInputs.length;
  return <div ref={containerRef} className="flex flex-col items-center justify-center w-full max-w-md mx-auto" data-id="element-7909">
      <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-indigo-100 w-full" data-id="element-7910">
        <div className="flex justify-between items-center mb-6" data-id="element-7911">
          <h3 className="text-lg font-bold text-slate-700" data-id="element-7912">
            {OP_LABELS[operation]}
          </h3>
          <div className="text-sm text-slate-500 font-medium bg-slate-100 px-3 py-1 rounded-full" data-id="element-7913">
            Vul de vakjes in
          </div>
        </div>

        <div className="flex justify-center" data-id="element-7914">
          <div className="grid gap-x-2 gap-y-1" style={{
          gridTemplateColumns: `repeat(${gridWidth}, minmax(40px, 1fr))`
        }} data-id="element-7915">
            {/* Carry row */}
            {Array.from({
            length: gridWidth
          }).map((_, i) => <div key={`carry-${i}`} className="h-8 flex items-end justify-center mb-1" data-id="element-7916">
                <input ref={el => carryRefs.current[i] = el} type="text" inputMode="numeric" className="w-6 h-6 text-center text-xs text-indigo-600 font-bold bg-indigo-50 border border-indigo-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400" maxLength={1} value={carryInputs[i] || ''} onChange={e => handleCarryChange(i, e.target.value)} data-id="element-7917" />
              </div>)}

            {/* First number */}
            {paddedNum1.split('').map((digit, i) => <div key={`n1-${i}`} className="h-12 flex items-center justify-center text-2xl font-bold text-slate-800 font-mono" data-id="element-7918">
                {digit}
              </div>)}

            {/* Second number with operator */}
            {paddedNum2.split('').map((digit, i) => <div key={`n2-${i}`} className="h-12 flex items-center justify-center text-2xl font-bold text-slate-800 font-mono relative" data-id="element-7919">
                {i === 0 && <span className="absolute -left-6 top-1/2 -translate-y-1/2 text-slate-400 font-sans" data-id="element-7920">
                    {operation}
                  </span>}
                {digit}
              </div>)}

            {/* Divider line */}
            <div className="col-span-full h-0.5 bg-slate-800 my-1 rounded-full" data-id="element-7921" />

            {/* Answer inputs — right-aligned with spacers if result has fewer digits */}
            {answerOffset > 0 && Array.from({
            length: answerOffset
          }).map((_, i) => <div key={`spacer-${i}`} className="h-14" data-id="element-7922" />)}
            {userInputs.map((val, i) => <div key={`input-${i}`} className="h-14 flex items-center justify-center" data-id="element-7923">
                <input ref={el => inputRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={val} onChange={e => handleInputChange(i, e.target.value)} onKeyDown={e => {
              if (e.key === 'ArrowLeft' && i > 0) inputRefs.current[i - 1]?.focus();
              if (e.key === 'ArrowRight' && i < userInputs.length - 1) inputRefs.current[i + 1]?.focus();
              if (e.key === 'Backspace' && !val && i > 0) inputRefs.current[i - 1]?.focus();
            }} className="w-full h-full text-center text-2xl font-bold rounded-xl border-2 transition-all focus:outline-none focus:ring-4 focus:ring-indigo-100 bg-white border-slate-300 text-slate-900 focus:border-indigo-500" data-id="element-7924" />
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}